import {
  FC,
  memo,
  ReactElement,
  Suspense,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { ADVANCED_SPORT_BY_COMPETITIONS_LAYOUT_SHOW_EVENTS_BUTTON_ID } from 'utils/constants/sportsbook/advanced';
import { SPORTSBOOK_VIEW_TYPES } from 'utils/constants/sportsbook/generic';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { fetchSportsConfigsData } from 'utils/generic/fetch-sports-configs-data';
import { lazy } from 'utils/generic/lazy';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import RidGenerator from 'utils/swarm/rid-generator';
import {
  ESportsbookLayoutTypes,
  ESportsbookPageTypes,
  TSportsbookConfigs
} from 'interfaces/sportsbook-configs';
import { executeCommand } from 'services/get-data-level';
import { defaultConfigs, SportsbookConfigsContext } from './Contexts';
import { Portal } from 'components/Shared/Portal';
import { FallbackContainer } from 'components/UI/Containers/FallbackContainer';
import { SportNFTsModal } from 'newcomponents/Shared/sportsbook/SportNFTsModal';
import { useBoostedOdds } from 'hooks/sportsbook/useBoostedOdds';
import { useSportsbookMountPoint } from 'newhooks/sportsbook/useSportsbookMountPoint';
import './index.less';

const { Esport } = lazy(
  () => import(/* webpackChunkName: "esport" */ './Esport')
);

const { FetchFavoriteTeamsData } = lazy(
  () =>
    import(
      /* webpackChunkName: "fetchFavoriteTeamsData" */ './__partials/FetchFavoriteTeamsData'
    )
);

const { Live } = lazy(() => import(/* webpackChunkName: "live" */ './Live'));

const { Prematch } = lazy(
  () => import(/* webpackChunkName: "prematch" */ './Prematch')
);

const { MetaUpdatesWrapper } = lazy(
  () =>
    import(
      /* webpackChunkName: "meta-updates" */ 'newcomponents/Shared/sportsbook/MetaUpdatesWrapper'
    )
);

const VIEWS: Record<string, FC> = {
  esport: Esport,
  live: Live,
  prematch: Prematch
};

export type TProps = {
  configs: TSportsbookConfigs;
};

export const Sportsbook = memo(
  ({ configs = defaultConfigs }: TProps): ReactElement => {
    const _history = useHistory();
    const [type] = useState(SPORTSBOOK_VIEW_TYPES[configs.pageType]);

    useSportsbookMountPoint();
    useBoostedOdds(configs.category === 'sport');

    const View = useMemo(() => VIEWS[type], [type]);

    const elementConfigs: TSportsbookConfigs = JSON.parse(
      JSON.stringify(configs)
    );

    elementConfigs.spbLayoutType = SpringConfigs.IS_AMERICAN_LAYOUT
      ? ESportsbookLayoutTypes.AMERICAN
      : configs.spbLayoutType || ESportsbookLayoutTypes.CLASSIC;

    if (
      elementConfigs.pageType === ESportsbookPageTypes.esport &&
      elementConfigs.category !== 'esport'
    ) {
      elementConfigs.category = 'esport';
    }

    useEffect(() => {
      window.fromNext &&
        _history.replace(`${window.getPathname()}${window.location.search}`);
    }, [_history]);

    useEffect((): void => {
      fetchSportsConfigsData();
    }, []);

    useEffect((): void => {
      const fromStatistics = JSON.parse(
        LocalStorage.getItem(storageKeyName('sportsbook', 'FROM_STATISTICS'))
      );

      if (fromStatistics?.sport) {
        executeCommand(
          RidGenerator.gForCommand(),
          {
            sport: ['alias'],
            region: ['alias']
          },
          {
            sport: {
              id: +fromStatistics.sport
            },
            game: {
              id: +fromStatistics.game
            },
            competition: {
              id: +fromStatistics.competition
            },
            region: {
              id: +fromStatistics.region
            }
          },
          (data: {
            data: { sport: { alias: string; region: { alias: string }[] }[] };
          }) => {
            const sport =
              data?.data.sport && Object.values(data?.data?.sport)[0];

            const sportAlias = sport.alias;
            const region = Object.values(sport.region)[0].alias;
            window.location.href = `${
              SpringConfigs.PAGE_URLS[
                +fromStatistics.type === 1 ? 'live' : 'prematch'
              ]
            }/${SpringConfigs.SPORTSBOOK_MOUNT_PATH}/${sportAlias}/${region}/${
              fromStatistics.competition
            }/${fromStatistics.game}`;
          }
        );
        LocalStorage.removeItem(
          storageKeyName('sportsbook', 'FROM_STATISTICS')
        );
      }
    }, [window.location.pathname]);

    return (
      <>
        <Portal>
          <div id="sportsbookScrollTopPosition" />
        </Portal>
        {configs.favoriteTeam ? (
          <Suspense fallback={null}>
            <FetchFavoriteTeamsData />
          </Suspense>
        ) : null}
        <div id="sportsbookWrapper" className="sportsbookWrapper">
          <SportsbookConfigsContext.Provider value={elementConfigs}>
            <Suspense fallback={<FallbackContainer />}>
              {SpringConfigs.INCLUDE_SPORT_DATA_IN_META && (
                <MetaUpdatesWrapper />
              )}
              <SportNFTsModal />
              <View />
            </Suspense>
          </SportsbookConfigsContext.Provider>
        </div>
        {isMobile() &&
          elementConfigs.spbLayoutType === ESportsbookLayoutTypes.ADVANCED && (
            <div
              id={ADVANCED_SPORT_BY_COMPETITIONS_LAYOUT_SHOW_EVENTS_BUTTON_ID}
            />
          )}
      </>
    );
  }
);
